import { useState, useEffect } from 'react';
import { X, FolderPlus, Info, Loader } from 'lucide-react';

const PRIORITIES = [
  { value: 'LOW',    label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH',   label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

/**
 * CreateCaseModal — create a new investigation.
 *
 * Props:
 *   isOpen   — boolean
 *   onClose  — () => void
 *   onCreate — async (payload) => void   payload: { title, description?, priority, eventId? }
 *   event    — optional event object; when present the case is seeded with it
 *              and an info row shows what will be included.
 */
export default function CreateCaseModal({ isOpen, onClose, onCreate, event = null }) {
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority]       = useState('NORMAL');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  // Reset form each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setDescription('');
    setPriority('NORMAL');
    setSaving(false);
    setError('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onCreate({
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        eventId: event?.id,
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to create case');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div className="inv-modal">
        <div className="inv-modal-header">
          <div className="inv-modal-title">
            <FolderPlus size={16} />
            <span>New Case</span>
          </div>
          <button className="inv-pane-close" onClick={onClose} disabled={saving}>
            <X size={16} />
          </button>
        </div>

        <div className="inv-modal-body">
          {event && (
            <div className="inv-info-row">
              <Info size={14} />
              <span>
                Will include: <strong>{event.eventTypeLabel}</strong>
                {event.actorName ? <> — {event.actorName}</> : null}
              </span>
            </div>
          )}

          <div className="inv-field">
            <label>
              Title <span className="inv-field-req">*</span>
            </label>
            <input
              type="text"
              autoFocus
              placeholder="e.g. After-hours access at Kitchen exit"
              value={title}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
            />
          </div>

          <div className="inv-field">
            <label>Description</label>
            <textarea
              placeholder="Optional context for this investigation…"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          <div className="inv-field">
            <label>Priority</label>
            <select value={priority} onChange={e => setPriority(e.target.value)}>
              {PRIORITIES.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="inv-info-row" style={{ color: '#ef4444' }}>
              <Info size={14} style={{ color: '#ef4444' }} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="inv-modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? <Loader size={14} className="spin" /> : null}
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
